import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Service Area", href: "#service-area" },
  { label: "Pricing", href: "#pricing" },
  { label: "About", href: "#about" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeAnd = (fn) => () => {
    setOpen(false);
    fn?.();
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-40 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-gold-soft/50 shadow-[0_4px_20px_-12px_rgba(26,42,79,0.15)]"
          : "bg-white/80 backdrop-blur border-b border-transparent"
      }`}
    >
      <div className="container-tn flex items-center justify-between h-32">
        <a href="#top" className="flex items-center gap-3 min-w-0" data-testid="nav-logo-link">
          <img
            src={BUSINESS.logoUrl}
            alt="Trusted Notary Illinois seal"
            className="h-24 w-24 rounded-full object-cover ring-1 ring-gold-soft/60"
            loading="eager"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-navy uppercase tracking-[0.2em] text-sm font-bold">
              Trusted Notary
            </span>
            <span className="font-serif text-gold uppercase tracking-[0.3em] text-[10px]">
              Illinois
            </span>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative font-sans text-sm font-medium text-ink hover:text-navy tracking-wide after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-gold hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneHref}`}
            data-testid="nav-call-link"
            className="flex items-center gap-2 text-navy font-sans font-semibold text-sm hover:text-gold transition-colors"
          >
            <Phone className="w-4 h-4" strokeWidth={2.2} />
            {BUSINESS.phoneDisplay}
          </a>
          <a
            href={BUSINESS.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-button"
            className="btn-gold !py-2.5 !px-5 !text-sm"
          >
            Book Online
          </a>
        </div>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-menu-toggle"
          className="lg:hidden p-2 rounded-md text-navy hover:bg-surface"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gold-soft/40 bg-white" data-testid="nav-mobile-menu">
          <div className="container-tn py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={closeAnd()}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="py-3 px-2 font-sans text-base text-ink hover:text-navy hover:bg-surface rounded-md tracking-wide"
              >
                {l.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 pt-3 border-t border-gold-soft/30 mt-2">
              <a href={`tel:${BUSINESS.phoneHref}`} className="btn-navy-outline" data-testid="nav-mobile-call">
                <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
              </a>
              <a
                href={BUSINESS.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                data-testid="nav-mobile-book"
              >
                Book Online
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
